/**
 * Project settings → Route patterns (pillar B). Patterns group step URLs
 * into pages for the hotspot map (`/docs/:slug` makes every docs page
 * one page). Suggestions come from the project's own step URLs and
 * are only applied when someone accepts them.
 */
import { normalizePattern } from "@iamu/shared";
import { Route } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Chip } from "@/components/ui/chip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useProjectRole } from "@/lib/use-project-role";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function RoutePatternsSection({ projectId }: { projectId: string }) {
  const utils = trpc.useUtils();
  const { canEdit } = useProjectRole(projectId);
  const { data, isLoading } = trpc.routePattern.list.useQuery({ projectId });
  const done = (message: string) => (r: { recompute: string }) => {
    utils.routePattern.list.invalidate({ projectId });
    utils.hotspot.invalidate();
    toast.success(
      r.recompute === "queued"
        ? `${message}. Page keys are being recomputed.`
        : message,
    );
  };
  const add = trpc.routePattern.add.useMutation({
    onSuccess: done("Pattern added"),
    onError: (e) => toastTrpcError(e, "Failed to add route pattern"),
  });
  const remove = trpc.routePattern.remove.useMutation({
    onSuccess: done("Pattern removed"),
    onError: (e) => toastTrpcError(e, "Failed to remove route pattern"),
  });

  const [dismissedPatterns, setDismissedPatterns] = useState<Set<string>>(
    () => {
      if (typeof window === "undefined") return new Set();
      try {
        const raw = window.localStorage.getItem(
          `iamu:dismissed-route-patterns:${projectId}`,
        );
        return raw ? new Set(JSON.parse(raw)) : new Set();
      } catch {
        return new Set();
      }
    },
  );

  const dismiss = (pattern: string) => {
    setDismissedPatterns((prev) => {
      const next = new Set(prev).add(pattern);
      try {
        window.localStorage.setItem(
          `iamu:dismissed-route-patterns:${projectId}`,
          JSON.stringify([...next]),
        );
      } catch {
        // storage unavailable
      }
      return next;
    });
  };

  const restoreDismissed = () => {
    setDismissedPatterns(new Set());
    try {
      window.localStorage.removeItem(
        `iamu:dismissed-route-patterns:${projectId}`,
      );
    } catch {
      // storage unavailable
    }
    toast.success("Dismissed suggestions restored");
  };

  if (isLoading || !data) {
    return <Skeleton className="h-24 w-full rounded-2xl" />;
  }

  const visibleSuggestions = (data.suggestions ?? []).filter(
    (s) => !dismissedPatterns.has(s.pattern),
  );
  const dismissedCount = (data.suggestions ?? []).filter((s) =>
    dismissedPatterns.has(s.pattern),
  ).length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground">
        {data.stepsWithUrl === 0
          ? "No step has a page URL yet. Runs from newer iamu CLI versions record the page each step was on; then suggestions appear here."
          : `${plural(data.stepsWithUrl, "step")} have a page URL. The first pattern that matches a step names its page; other URLs keep their path with ids replaced.`}
      </p>

      <Card>
        <CardContent className="p-0">
          {data.patterns.length === 0 ? (
            <p className="px-6 py-4 text-sm text-muted-foreground">
              No patterns yet.
            </p>
          ) : (
            <ol
              aria-label="Route patterns, in match order"
              className="divide-y divide-border/60"
            >
              {data.patterns.map((p) => (
                <li key={p.id} className="flex items-center gap-4 px-6 py-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="break-all font-mono text-sm font-semibold">
                      {p.pattern}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      {p.label && (
                        <span className="text-foreground">{p.label}</span>
                      )}
                      <span className="tabular-nums">
                        Matches {plural(p.steps, "step")}
                      </span>
                      {p.source === "suggested" && (
                        <Chip tone="outline">Accepted suggestion</Chip>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={remove.isPending}
                      aria-label={`Remove ${p.pattern}`}
                      onClick={() => remove.mutate({ id: p.id })}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {(visibleSuggestions.length > 0 || dismissedCount > 0) && (
        <section aria-labelledby="route-suggestions" className="space-y-2">
          <div className="flex items-center justify-between">
            <h3
              id="route-suggestions"
              className="text-sm font-medium text-muted-foreground"
            >
              Suggested from your step URLs
            </h3>
            {dismissedCount > 0 && canEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                onClick={restoreDismissed}
              >
                Reset dismissed ({dismissedCount})
              </Button>
            )}
          </div>
          {visibleSuggestions.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y divide-border/60">
                  {visibleSuggestions.map((s) => (
                    <li
                      key={s.pattern}
                      className="flex items-center gap-4 px-6 py-3"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="break-all font-mono text-sm font-semibold">
                          {s.pattern}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {plural(s.steps, "step")}, {s.distinct} different
                          values · e.g. {s.examples.join(", ")}
                        </p>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Dismiss ${s.pattern}`}
                            onClick={() => dismiss(s.pattern)}
                          >
                            Dismiss
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={add.isPending}
                            aria-label={`Accept ${s.pattern}`}
                            onClick={() =>
                              add.mutate({
                                projectId,
                                pattern: s.pattern,
                                source: "suggested",
                              })
                            }
                          >
                            Accept
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {canEdit && (
        <AddPatternForm
          pending={add.isPending}
          onAdd={(pattern, label) =>
            add.mutate({ projectId, pattern, label, source: "user" })
          }
        />
      )}
    </div>
  );
}

function AddPatternForm({
  pending,
  onAdd,
}: {
  pending: boolean;
  onAdd: (pattern: string, label: string | undefined) => void;
}) {
  const [pattern, setPattern] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizePattern(pattern);
    if (!normalized) {
      setError("Use a path like /docs/:slug: literal segments or :name.");
      return;
    }
    setError(null);
    onAdd(normalized, label.trim() || undefined);
    setPattern("");
    setLabel("");
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <fieldset className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <legend className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
          <Route className="size-4" aria-hidden="true" />
          Add a pattern
        </legend>
        <div className="space-y-1.5">
          <Label htmlFor="route-pattern">Pattern</Label>
          <Input
            id="route-pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="/docs/:slug"
            aria-invalid={!!error}
            aria-describedby={error ? "route-pattern-error" : undefined}
            className="font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="route-label">Label (optional)</Label>
          <Input
            id="route-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={pending || !pattern.trim()}>
          Add pattern
        </Button>
      </fieldset>
      {error && (
        <p id="route-pattern-error" className="text-sm text-severity-critical">
          {error}
        </p>
      )}
    </form>
  );
}
